import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ncp_admin_token";

const AuthCtx = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const authedAxios = useCallback(
    () =>
      axios.create({
        baseURL: API,
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }),
    [token]
  );

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token) {
        setUser(null);
        setChecking(false);
        return;
      }
      try {
        const r = await axios.get(`${API}/admin/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setUser(r.data);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(
      `${API}/admin/login`,
      { email, password },
      { withCredentials: true }
    );
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(
        `${API}/admin/logout`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
    } catch {
      /* swallow */
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, [token]);

  return (
    <AuthCtx.Provider
      value={{ token, user, checking, login, logout, authedAxios }}
    >
      {children}
    </AuthCtx.Provider>
  );
};

export const useAdminAuth = () => {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return v;
};

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
